import Vue from 'vue';
import $web3js from '../web3.js';
import $bizConfig from '@/models/bizConfig.js';
import $utils from "@/lib/utils/index.js";

const erc20Contract = require('./erc20Contract.json')

let web3 = $web3js.getWeb3();
let contractMap = new Map();
let decimalsMap = new Map();
let symbolMap = new Map();
let nameMap = new Map();
let totalSupplyMap = new Map();
let tokenMap = new Map();
let tokenBySymbolMap = new Map();

const handleContractEmpty = (_defaultValue) => {
    return new Promise((resolve, reject) => {
        resolve(_defaultValue);
    })
}

const getErc20Abi = (funcName) => {
    let arrs = erc20Contract.abi.filter((value, key, values) => {
        return funcName === value.name
    })
    return arrs && arrs.length > 0 ? arrs[0] : null;
}

const getErc20Contract = (_contractAddress) => {
    let _erc20Contract = contractMap.get(_contractAddress.toUpperCase());
    if (!_erc20Contract) {
        try {
            _erc20Contract = web3 ? new web3.eth.Contract(erc20Contract.abi, _contractAddress) : '';
        } catch (error) {
            Vue.prototype.$toast(error && error.message ? error.message : error);
        }
        if (!!_erc20Contract) {
            contractMap.set(_contractAddress.toUpperCase(), _erc20Contract)
        }
    }
    return _erc20Contract;
}

const fromErc20Value = async(_value, _contractAddress) => {
    let _decimals = await getErc20Decimals([_contractAddress]);
    return Vue.prototype.$numberUtils.movePointLeft(_value, _decimals);
}

const toErc20Value = async(_value, _contractAddress) => {
    let _decimals = await getErc20Decimals([_contractAddress]);
    return Vue.prototype.$numberUtils.format(Vue.prototype.$numberUtils.movePointRight(_value, _decimals), 0, 1);
}

const getErc20Name = async([_contractAddress], options) => {
    let _name = nameMap.get(_contractAddress.toUpperCase());
    if (!_name) {
        let contract = getErc20Contract(_contractAddress);
        if (!!contract) {
            _name = await $web3js.call(contract.methods.name, [], options);
            if (!!_name) {
                nameMap.set(_contractAddress.toUpperCase(), _name)
            }
        } else {
            return handleContractEmpty("");
        }
    }
    return _name;
}

const getErc20NameCall = ([_contractAddress]) => {
    return [_contractAddress, $web3js.encodeFunctionCall(getErc20Abi('name'), [])];
}

const getErc20Symbol = async([_contractAddress], options) => {
    let _symbol = symbolMap.get(_contractAddress.toUpperCase());
    if (!_symbol) {
        let contract = getErc20Contract(_contractAddress);
        if (!!contract) {
            _symbol = await $web3js.call(contract.methods.symbol, [], options);
            if (!!_symbol) {
                symbolMap.set(_contractAddress.toUpperCase(), _symbol)
            }
        } else {
            return handleContractEmpty("");
        }
    }
    return _symbol;
}

const getErc20SymbolCall = ([_contractAddress]) => {
    return [_contractAddress, $web3js.encodeFunctionCall(getErc20Abi('symbol'), [])];
}

const getErc20Decimals = async([_contractAddress], options) => {
    let _decimals = decimalsMap.get(_contractAddress.toUpperCase());
    if (!_decimals) {
        let contract = getErc20Contract(_contractAddress);
        if (!!contract) {
            _decimals = await $web3js.call(contract.methods.decimals, [], options);
            if (!!_decimals) {
                decimalsMap.set(_contractAddress.toUpperCase(), _decimals)
            }
        } else {
            return handleContractEmpty(null);
        }
    }
    return _decimals;
}

const getErc20DecimalsCall = ([_contractAddress]) => {
    return [_contractAddress, $web3js.encodeFunctionCall(getErc20Abi('decimals'), [])];
}

const getErc20TotalSupply = async([_contractAddress, reLoad], options) => {
    let _totalSupply = totalSupplyMap.get(_contractAddress.toUpperCase());
    if (!_totalSupply || !!reLoad) {
        let contract = getErc20Contract(_contractAddress);
        if (!!contract) {
            _totalSupply = await $web3js.call(contract.methods.totalSupply, [], options, fromErc20Value, [_contractAddress]);
            if (!!_totalSupply) {
                totalSupplyMap.set(_contractAddress.toUpperCase(), _totalSupply)
            }
        } else {
            return handleContractEmpty(null);
        }
    }
    return _totalSupply;
}

const getErc20TotalSupplyCall = ([_contractAddress]) => {
    return [_contractAddress, $web3js.encodeFunctionCall(getErc20Abi('totalSupply'), [])];
}

const getErc20Balance = async([_contractAddress, _owner], options) => {
    let contract = getErc20Contract(_contractAddress);
    if (!contract) {
        return handleContractEmpty("");
    }
    return $web3js.call(contract.methods.balanceOf, [_owner], options, fromErc20Value, [_contractAddress]);
}

const getErc20BalanceCall = ([_contractAddress, _owner]) => {
    return [_contractAddress, $web3js.encodeFunctionCall(getErc20Abi('balanceOf'), [_owner])];
}

const getErc20Allowance = async([_contractAddress, owner, spender], options) => {
    let contract = getErc20Contract(_contractAddress);
    if (!contract) {
        return handleContractEmpty("");
    }
    return $web3js.call(contract.methods.allowance, [owner, spender], options, fromErc20Value, [_contractAddress]);
}

const getErc20AllowanceCall = ([_contractAddress, owner, spender]) => {
    return [_contractAddress, $web3js.encodeFunctionCall(getErc20Abi('allowance'), [owner, spender])];
}

const erc20Approve = async([_contractAddress, _spender, _value], options, callback) => {
    let contract = getErc20Contract(_contractAddress);
    if (!contract) {
        return handleContractEmpty("");
    }
    _value = await toErc20Value(_value, _contractAddress);
    return $web3js.send(contract.methods.approve, [_spender, _value], options, callback);
}

const erc20ApproveEstimateSend = async([_contractAddress, _spender, _value], options) => {
    let contract = getErc20Contract(_contractAddress);
    if (!contract) {
        return handleContractEmpty("");
    }
    _value = await toErc20Value(_value, _contractAddress);
    let gasPromise = $web3js.estimateSend(contract.methods.approve, [_spender, _value], options);
    let gasPricePromise = $web3js.getGasPrice();

    let [e1, gas] = await $utils.awaitWrap(gasPromise);
    let [e2, gasPrice] = await $utils.awaitWrap(gasPricePromise);
    if (!!e1) {
        Vue.prototype.$toast(e1 && e1.message ? e1.message : e1);
        return null;
    }

    let fee = $web3js.fromWei(Vue.prototype.$numberUtils.mul(gasPrice, gas));
    return { gas, gasPrice, fee };
}

const erc20Transfer = async([_contractAddress, _to, _value], options, callback) => {
    let contract = getErc20Contract(_contractAddress);
    if (!contract) {
        return handleContractEmpty("");
    }
    _value = await toErc20Value(_value, _contractAddress);
    return $web3js.send(contract.methods.transfer, [_to, _value], options, callback);
}

const erc20TransferEstimateSend = async([_contractAddress, _to, _value], options) => {
    let contract = getErc20Contract(_contractAddress);
    if (!contract) {
        return handleContractEmpty("");
    }
    _value = await toErc20Value(_value, _contractAddress);
    return $web3js.estimateSend(contract.methods.approve, [_to, _value], options);
}

const initErc20Map = () => {
    if (tokenMap.size == 0) {
        let _tokens = $bizConfig.erc20List
        if (_tokens.length > 0) {
            _tokens.forEach((_token) => {
                tokenMap.set(_token.address.toUpperCase(), _token);
                tokenBySymbolMap.set(_token.symbol.toUpperCase(), _token);
                decimalsMap.set(_token.address.toUpperCase(), _token.decimals);
                symbolMap.set(_token.address.toUpperCase(), _token.symbol);
                nameMap.set(_token.address.toUpperCase(), _token.name);
            })
        }
    }
}

const getErc20Info = (address) => {
    if (!address) {
        return undefined;
    }
    return tokenMap.get(address.toUpperCase());
}

const getErc20Address = (symbol) => {
    if (!symbol) {
        return undefined;
    }
    let _token = tokenBySymbolMap.get(symbol.toUpperCase())
    return !!_token ? _token.address : undefined;
}

initErc20Map();

export default {
    getErc20Abi,
    fromErc20Value,
    toErc20Value,
    getErc20Contract,
    getErc20Name,
    getErc20NameCall,
    getErc20Symbol,
    getErc20SymbolCall,
    getErc20Decimals,
    getErc20DecimalsCall,
    getErc20TotalSupply,
    getErc20TotalSupplyCall,
    getErc20Balance,
    getErc20BalanceCall,
    getErc20Allowance,
    getErc20AllowanceCall,
    erc20Approve,
    erc20ApproveEstimateSend,
    erc20Transfer,
    erc20TransferEstimateSend,
    getErc20Info,
    getErc20Address,
}


// WEBPACK FOOTER //
// ./src/lib/contract/erc20/index.js